import { useContext, useState, useEffect, useRef } from "react";
import AppContext from "AppContext";
import styled, { css } from "styled-components";
import Panels from "views/panels/panels";
import ToolHeading from "views/tools/ToolHeading";
import AIChat from "views/tools/AIChat";
import BhatikaIcon from "assets/bhatika.png";
import TuneIcon from '@mui/icons-material/Tune';
import StyleIcon from '@mui/icons-material/Style';

export default function ToolContent(props) {
  const { isMobile } = useContext(AppContext);
  const { toolItem, open, handleOnClose } = props;
  const isChat = toolItem?.component === "AIChat";
  const [chatLoading, setChatLoading] = useState(false);
  const [controlsState, setControlsState] = useState({
    height: '0px',
    width: '25vw',
    position: 'right'
  });
  const [isControlsOpen, setIsControlsOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const controlsRef = useRef(null);
  const savedHeight = useRef(null);
  const dragStartX = useRef(0);
  const dragStartY = useRef(0);
  const dragStartWidth = useRef(0);
  const dragStartHeight = useRef(0);

  useEffect(() => {
    if (!open) {
      setControlsState(prev => ({ ...prev, height: '0px' }));
    } else {
      setControlsState(prev => ({ ...prev, height: '0px' }));
      requestAnimationFrame(() => {
        if (isMobile) {
          savedHeight.current = '85vh';
        } else if (!savedHeight.current || savedHeight.current === '0px') {
          savedHeight.current = isChat ? '70vh' : '50vh';
        }
        setControlsState(prev => ({
          ...prev,
          height: savedHeight.current
        }));
      });
    }
  }, [open, isMobile, isChat]);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isDragging) {
        e.preventDefault();
        const deltaX = e.clientX - dragStartX.current;
        const deltaY = dragStartY.current - e.clientY;
        
        const newWidth = controlsState.position === 'right'
          ? Math.max(395, dragStartWidth.current - deltaX)
          : Math.max(395, dragStartWidth.current + deltaX);
          
        const newHeight = Math.max(300, dragStartHeight.current + deltaY);
        
        savedHeight.current = `${newHeight}px`;
        
        setControlsState(prev => ({
          ...prev,
          width: `${newWidth}px`,
          height: `${newHeight}px`
        }));
      }
    };

    const handleMouseUp = () => {
      if (isDragging) {
        savedHeight.current = controlsState.height;
      }
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, controlsState.position]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (controlsRef.current && !controlsRef.current.contains(event.target)) {
        setIsControlsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleDragStart = (e) => {
    e.preventDefault();
    setIsDragging(true);
    dragStartX.current = e.clientX;
    dragStartY.current = e.clientY;
    
    const currentWidth = controlsState.width;
    const currentHeight = controlsState.height;
    
    if (currentWidth.includes('vw')) {
      const vwValue = parseFloat(currentWidth);
      dragStartWidth.current = (window.innerWidth * vwValue) / 100;
    } else {
      dragStartWidth.current = parseInt(currentWidth);
    }
    
    if (currentHeight.includes('vh')) {
      const vhValue = parseFloat(currentHeight);
      dragStartHeight.current = (window.innerHeight * vhValue) / 100;
    } else {
      dragStartHeight.current = parseInt(currentHeight);
    }
  };

  const handleSizeChange = (newHeight, newWidth) => {
    if (newHeight) {
      savedHeight.current = newHeight;
    }
    setControlsState(prev => ({
      ...prev,
      ...(newHeight && { height: newHeight }),
      ...(newWidth && { width: newWidth })
    }));
  };

  const handlePositionChange = (newPosition) => {
    setControlsState(prev => ({
      ...prev,
      position: newPosition
    }));
  };

  const renderContent = () => {
    if (isChat) {
      return <AIChat onLoadingChange={setChatLoading} />;
    }
    return <Panels toolItem={toolItem} showFilters={toolItem?.showFilters} />;
  };

  const getIcon = () => {
    if (isChat) {
      return <img src={BhatikaIcon} alt="Bhatika" style={{ width: "24px", height: "24px", marginRight: "8px" }} />;
    }
    return null;
  };

  if (!toolItem) return null;

  return (
    <DrawerContainer
      open={open}
      $height={controlsState.height}
      $width={toolItem?.width === "full-width" ? "calc(100% - 325px)" : controlsState.width}
      $position={controlsState.position}
      $isDragging={isDragging}
    >
      <DragHandleCorner 
        $position="left"
        onMouseDown={handleDragStart}
      />
      <DragHandleCorner
        $position="right"
        onMouseDown={handleDragStart}
      />
      <ToolContainer>
        <ToolHeading
          title={toolItem?.text}
          description={toolItem?.subtitle}
          showCloseIcon
          icon={getIcon()}
          onClose={handleOnClose}
          spinning={chatLoading}
        >
          <SizeControls ref={controlsRef} onClick={() => setIsControlsOpen(!isControlsOpen)}>
            <ControlsIcon><StyleIcon /></ControlsIcon>
            {isControlsOpen && (
              <ControlsWrapper>
                <ControlGroup>
                  <OptionGroup>
                    <ControlOption
                      onClick={(e) => { e.stopPropagation(); handlePositionChange('left'); setIsControlsOpen(false); }}
                      $active={controlsState.position === 'left'}
                    >
                      Align Left
                    </ControlOption>
                    <ControlOption
                      onClick={(e) => { e.stopPropagation(); handlePositionChange('right'); setIsControlsOpen(false); }}
                      $active={controlsState.position === 'right'}
                    >
                      Align Right
                    </ControlOption>
                  </OptionGroup>
                  <ControlDivider />
                  <OptionGroup>
                    <ControlOption
                      onClick={(e) => { e.stopPropagation(); handleSizeChange(null, '25vw'); setIsControlsOpen(false); }}
                      $active={controlsState.width === '25vw'}
                    >
                      Narrow width
                    </ControlOption>
                    <ControlOption
                      onClick={(e) => { e.stopPropagation(); handleSizeChange(null, '50vw'); setIsControlsOpen(false); }}
                      $active={controlsState.width === '50vw'}
                    >
                      Medium width
                    </ControlOption>
                    <ControlOption
                      onClick={(e) => { e.stopPropagation(); handleSizeChange(null, '90vw'); setIsControlsOpen(false); }}
                      $active={controlsState.width === '90vw'}
                    >
                      Full width
                    </ControlOption>
                  </OptionGroup>
                  <ControlDivider />
                  <OptionGroup>
                    <ControlOption
                      onClick={(e) => { e.stopPropagation(); handleSizeChange('50vh'); setIsControlsOpen(false); }}
                      $active={controlsState.height === '50vh'}
                    >
                      Normal Height
                    </ControlOption>
                    <ControlOption
                      onClick={(e) => { e.stopPropagation(); handleSizeChange('70vh'); setIsControlsOpen(false); }}
                      $active={controlsState.height === '70vh'}
                    >
                      Medium Height
                    </ControlOption>
                    <ControlOption
                      onClick={(e) => { e.stopPropagation(); handleSizeChange('85vh'); setIsControlsOpen(false); }}
                      $active={controlsState.height === '85vh'}
                    >
                      Tall Height
                    </ControlOption>
                  </OptionGroup>
                </ControlGroup>
              </ControlsWrapper>
            )}
          </SizeControls>
        </ToolHeading>
        <ContentContainer $isDark={isChat}>
          {renderContent()}
        </ContentContainer>
      </ToolContainer>
    </DrawerContainer>
  );
}

const DrawerContainer = styled.div.attrs({
  className: 'DrawerContainer'
})`
  position: absolute;
  bottom: calc(100% + 0px);
  ${props => props.$position === 'left' ? 'left: 5%;' : 'right: 5%;'}
  width: ${props => props.$width};
  min-width: 395px;
  pointer-events: none;
  overflow-x: hidden;
  overflow-y: hidden;
  display: ${props => props.open ? 'flex' : 'none'};
  height: ${props => props.$height};
  pointer-events: ${props => props.open ? 'all' : 'none'};
  font-family: PT Sans;
  box-shadow: 0px 0px 5px rgba(0, 0, 0, 0.1);
  transition: ${props => props.$isDragging ? 'none' : 'width 0.15s ease-out, height 0.15s ease-out'};
  user-select: ${props => props.$isDragging ? 'none' : 'auto'};
  will-change: transform, width, height;
  transform: translateZ(0);
  contain: layout size;
  
  @media screen and (max-width: 640px) {
    left: 0;
    bottom: 70px;
    width: 100%;
  }
`;

const ToolContainer = styled.div.attrs({
  className: 'ToolContainer'
})`
  position: relative;
  height: 100%;
  width: 100%;
  background: transparent;
`;

const ContentContainer = styled.div.attrs({
  className: 'ContentContainer'
})`
  border-top-left-radius: 2px;
  border-top-right-radius: 2px;
  padding-top: 85.52px;
  box-sizing: border-box;
  height: 100%;
  box-shadow: 0px 0px 5px rgba(0, 0, 0, 0.2);
  background: ${props => props.$isDark ? '#131313' : 'white'};
  overflow: hidden;
  border-radius: 2px;
  background: white;
  
  @media screen and (min-width: 640px) {
    padding-top: 80px;
  }

  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: ${props => props.$isDark ? '#1a1a1a' : '#f1f1f1'};
    border-radius: 2px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${props => props.$isDark ? '#333' : '#888'};
    border-radius: 2px;
    
    &:hover {
      background: ${props => props.$isDark ? 'rgba(255,255,255,0.1)' : '#666'};
    }
  }
`;



const ControlDivider = styled.div.attrs({
  className: 'ControlDivider'
})`
  height: 1px;
  background: #e4e4e4;
  margin: 5px 0px;
  width: 100%;
  display: flex;
`;


const ControlsWrapper = styled.div.attrs({
  className: 'ControlsWrapper'
})`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 4px;
  z-index: 10;
  min-width: 135px;
  border-radius: 3px;
  padding: 5px 0px 8px;
  box-shadow: 0 0px 4px rgba(0,0,0,0.1);
`;

const ControlGroup = styled.div.attrs({
  className: 'ControlGroup'
})`
  display: flex;
  flex-direction: column;
  background: white;
  border-radius: 3px;
  max-height: 300px;
  overflow-y: auto;
  gap: 4px;
  padding: 0px 0px;
`;

const OptionGroup = styled.div.attrs({
  className: 'OptionGroup'
})`
  display: flex;
  flex-wrap: wrap;
  gap: 2px;
  padding: 0px;
`;

const ControlOption = styled.span.attrs({
  className: 'ControlOption'
})`
  background: ${props => props.$active ? 'black' : 'white'};
  // border: 1px solid ${props => props.$active ? 'black' : '#eee'};
  border-radius: 3px;
  margin: 2px 8px;
  padding: 5px 8px;
  text-align: left;
  cursor: pointer;
  font-size: 12px;
  font-weight: 900;
  color: ${props => props.$active ? 'white' : '#666'};
  white-space: nowrap;
  // width: calc(100% - 16px);
  width: fit-content;
  transition: all 0.15s ease;
  
  &:hover {
    background: ${props => props.$active ? 'black' : '#f5f5f5'};
    color: ${props => props.$active ? 'white' : 'black'};
    border-color: ${props => props.$active ? 'black' : '#ddd'};
  }
  
  &:active {
    background: ${props => props.$active ? 'black' : '#e0e0e0'};
  }
`;

const ControlsIcon = styled.div.attrs({
  className: 'ControlsIcon'
})`
  // margin-right: 8px;
  padding: 3px 5px 0px;
  svg {
    width: 24px;
  }
  `;

const SizeControls = styled.div.attrs({
  className: 'SizeControls'
})`
  position: relative;
  display: flex;
  align-items: center;
  background: #f7f7f7;
  padding: 0px;
  border-radius: 2px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.5px;
  transition: all 0.2s ease;
  color: #B2BEB5;
  cursor: pointer;

  &:hover {
    background: #efefef;
    color: black;
  }

  @media screen and (max-width: 768px) {
    display: none;
  }
`;

const DragHandleCorner = styled.div.attrs({
  className: 'DragHandleCorner'
})`
  position: absolute;
  top: 0;
  ${props => props.$position === 'left' ? 'left: 0' : 'right: 0'};
  width: 25px;
  height: 25px;
  background: transparent;
  z-index: 10;
  cursor: ${props => props.$position === 'left' ? 'nwse-resize' : 'nesw-resize'};
  border-radius: 3px;
  &::before {
    content: '';
    position: absolute;
    top: 0;
    ${props => props.$position === 'left' ? 'left: 0' : 'right: 0'};
    width: 100%;
    height: 100%;
    background: transparent;
    clip-path: ${props => props.$position === 'left' 
      ? 'polygon(0 0, 100% 0, 0 100%)'
      : 'polygon(0 0, 100% 0, 100% 100%)'};
  }

  &:hover::before {
    // background: rgba(0, 0, 0, 0.15);
  }
  
  @media screen and (max-width: 768px) {
    display: none;
  }
`;